// src/utils/noleggiUtils.js
// Unico posto dove vive la logica di calcolo costo/stato di un noleggio — riusata da
// RegistroNoleggiPage, ControlloEconomico e PianificazioneTeamPage, così i tre punti
// non possono mai calcolare cifre diverse per lo stesso noleggio.

// Converte un oggetto Date in stringa "YYYY-MM-DD" usando i componenti LOCALI
// (anno/mese/giorno), MAI toISOString() — quella converte sempre in UTC e in Italia,
// con l'ora legale (UTC+2), fa scivolare indietro di un giorno qualunque data vicina
// alla mezzanotte. È esattamente il bug appena trovato: usarla per ricavare la data da
// un oggetto Date costruito in locale ne cambiava il giorno senza che nessuno se ne accorgesse.
// Definizione unica ora in dateUtils.js (usata in tutta l'app, non solo qui) — importata
// e riesportata, così sia questo file che chi già importa da qui continuano a funzionare
// senza dover cambiare nulla.
import { toLocalDateStr } from "./dateUtils";
import { numIt } from "./economiaCommessa";
export { toLocalDateStr };

function oggiStr() { return toLocalDateStr(new Date()); }

// Giorni di calendario, inclusivo del giorno di inizio — usato solo per il testo "X giorni
// totali" mostrato accanto ai giorni davvero fatturati, quando le due cifre non coincidono
// (weekend/festivi/sospensioni esclusi). NON è più questo il calcolo che determina il costo:
// per quello vedi giorniFatturabiliNoleggio più sotto.
export function giorniTrascorsiNoleggio(dataInizio, dataFineEffettiva) {
  if (!dataInizio) return 0;
  const inizio = new Date(dataInizio + "T00:00:00");
  const fine = dataFineEffettiva ? new Date(dataFineEffettiva + "T00:00:00") : new Date();
  const diffMs = fine.setHours(0,0,0,0) - inizio.setHours(0,0,0,0);
  const giorni = Math.floor(diffMs / 86400000) + 1;
  return Math.max(1, giorni);
}

export function isWeekend(dataStr) {
  const g = new Date(dataStr + "T00:00:00").getDay();
  return g === 0 || g === 6;
}

// Domenica di Pasqua (algoritmo di Meeus/Jones/Butcher, calendario gregoriano) — nessuna
// tabella da mantenere, funziona per qualunque anno. Pasquetta è il giorno successivo.
function pasquaGregoriana(anno) {
  const a = anno % 19, b = Math.floor(anno / 100), c = anno % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19*a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2*e + 2*i - h - k) % 7;
  const m = Math.floor((a + 11*h + 22*l) / 451);
  const mese = Math.floor((h + l - 7*m + 114) / 31);
  const giorno = ((h + l - 7*m + 114) % 31) + 1;
  return new Date(anno, mese - 1, giorno); // mese 1-based nell'algoritmo, Date lo vuole 0-based
}

const FESTIVI_FISSI_MMGG = ["01-01","01-06","04-25","05-01","06-02","08-15","11-01","12-08","12-25","12-26"];

export function isFestivoItaliano(dataStr) {
  const mmgg = dataStr.slice(5);
  if (FESTIVI_FISSI_MMGG.includes(mmgg)) return true;
  const anno = parseInt(dataStr.slice(0, 4), 10);
  // 4 ottobre, San Francesco d'Assisi: festa nazionale dal 2026 (L. 151/2025)
  if (mmgg === "10-04" && anno >= 2026) return true;
  const pasqua = pasquaGregoriana(anno);
  const pasquetta = new Date(pasqua); pasquetta.setDate(pasquetta.getDate() + 1);
  return dataStr === toLocalDateStr(pasquetta);
}

// ─────────────────────────────────────────────────────────────────────────
// FASCIA ORARIA INTERVENTO — usata per i Contratti di Manutenzione, per
// proporre da sola la tariffa giusta (feriale/festivo/notturno) di un
// intervento. Riusa le stesse identiche funzioni sopra (isWeekend,
// isFestivoItaliano) già testate per i Noleggi, invece di duplicarle una
// seconda volta con il rischio di farle divergere.
//
// Priorità quando più condizioni coincidono (es. domenica notte): il
// notturno vince sempre, perché rappresenta l'orario più critico a
// prescindere dal giorno — poi il festivo (domenica + festività nazionali,
// il sabato resta feriale), infine il feriale come impostazione predefinita.
// Gli orari notturni sono configurabili per organizzazione (default
// 22:00–06:00, il più diffuso in pratica) perché la legge (D.Lgs. 66/2003)
// fissa solo l'obbligo di includere la fascia mezzanotte–5:00 dentro 7 ore
// consecutive, lasciando i confini esatti alla contrattazione collettiva.
export function classificaFasciaIntervento(dataStr, oraStr, orgSettings={}) {
  const inizioNotte = orgSettings.oraInizioNotturno || "22:00";
  const fineNotte = orgSettings.oraFineNotturno || "06:00";
  if (oraStr) {
    const dentroNotte = inizioNotte < fineNotte
      ? (oraStr >= inizioNotte && oraStr < fineNotte)
      : (oraStr >= inizioNotte || oraStr < fineNotte); // fascia che attraversa la mezzanotte
    if (dentroNotte) return "notturno";
  }
  if (isFestivoItaliano(dataStr) || new Date(dataStr+"T00:00:00").getDay()===0) return "festivo";
  return "feriale";
}

function inSospensione(dataStr, sospensioni) {
  return (sospensioni || []).some(s => s.dal && s.al && dataStr >= s.dal && dataStr <= s.al);
}

// Giorni davvero fatturabili tra due date — giorno per giorno, scartando weekend (se
// escludiWeekend), festivi italiani (se escludiFestivi) e giorni dentro un periodo di
// sospensione concordato col fornitore. Con tutte le esclusioni disattivate e nessuna
// sospensione, il risultato coincide con giorniTrascorsiNoleggio (comportamento di sempre).
export function giorniFatturabiliNoleggio(dataInizio, dataFineEffettiva, opts = {}) {
  if (!dataInizio) return 0;
  const { escludiWeekend = false, escludiFestivi = false, sospensioni = [] } = opts;
  const fineStr = dataFineEffettiva || oggiStr();
  if (fineStr < dataInizio) return 0;
  let cursor = new Date(dataInizio + "T00:00:00");
  const end = new Date(fineStr + "T00:00:00");
  let count = 0;
  while (cursor <= end) {
    const cStr = toLocalDateStr(cursor);
    const escluso = (escludiWeekend && isWeekend(cStr)) || (escludiFestivi && isFestivoItaliano(cStr)) || inSospensione(cStr, sospensioni);
    if (!escluso) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

// Stessa funzione sopra, ma prendendo le opzioni direttamente dal documento noleggio —
// comodo ovunque si abbia già l'oggetto "n" invece dei singoli campi.
export function giorniFatturabiliDiNoleggio(n) {
  return giorniFatturabiliNoleggio(n?.dataInizio, n?.dataFineEffettiva, {
    escludiWeekend: n?.escludiWeekend, escludiFestivi: n?.escludiFestivi, sospensioni: n?.sospensioni,
  });
}

// Costo del solo noleggio (tariffa), maturato ad oggi se attivo o finale se concluso — usa i
// giorni davvero fatturabili, non il calendario grezzo. Senza i trasporti.
export function costoNoloNoleggio(n) {
  if (!n) return 0;
  if (n.tariffaTipo === "forfettario") return numIt(n.importoForfettario) || 0;
  const giorni = giorniFatturabiliDiNoleggio(n);
  return giorni * (numIt(n.importoGiornaliero) || 0);
}

// Trasporti (W439): il noleggiatore fattura quasi sempre la consegna in cantiere e il ritiro.
// «Trasporto compreso» = già dentro la tariffa, quindi zero.
export function trasportiNoleggio(n) {
  if (!n || n.trasportoCompreso) return { consegna: 0, ritiro: 0, totale: 0 };
  const consegna = Math.max(0, numIt(n.trasportoConsegna) || 0);
  const ritiro = Math.max(0, numIt(n.trasportoRitiro) || 0);
  return { consegna, ritiro, totale: consegna + ritiro };
}

// Costo del noleggio = tariffa + trasporti. È il numero usato ovunque (Controllo Economico,
// Cabina di Regia, report, resoconto per fornitore): un solo calcolo, mai cifre diverse.
export function costoNoleggio(n) {
  if (!n) return 0;
  return costoNoloNoleggio(n) + trasportiNoleggio(n).totale;
}

// Errori dei campi trasporto (W439): la consegna va sempre indicata (anche 0 se il noleggiatore
// non la fa pagare), a meno che sia compresa nella tariffa. Il ritiro si conferma alla chiusura.
const importoValido = (t) => /^€?\s*(\d{1,3}(\.\d{3})+|\d+)([.,]\d{1,2})?$/.test(String(t).trim());
export function erroreTrasporti(f) {
  if (f.trasportoCompreso) return "";
  const c = String(f.trasportoConsegna ?? "").trim();
  if (c === "") return "Indica il costo del trasporto di consegna (0 se non c'è) oppure spunta «Trasporto compreso nella tariffa»";
  if (!importoValido(c)) return "Il costo del trasporto di consegna non è un importo valido (es. 250 o 250,00)";
  const r = String(f.trasportoRitiro ?? "").trim();
  if (r !== "" && !importoValido(r)) return "Il costo del trasporto di ritiro non è un importo valido (es. 250 o 250,00)";
  return "";
}
export { importoValido as importoTrasportoValido };

// Nome del noleggiatore come chiave di raggruppamento quando manca il collegamento all'elenco
const chiaveNome = (t) => String(t || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Resoconto economico per fornitore (W439). Un noleggio entra se è stato in corso almeno un
 * giorno nel periodo [dal, al] (date "AAAA-MM-GG", facoltative) e, se data, sulla commessa.
 * Gli importi sono quelli interi del noleggio (tariffa maturata + trasporti): è il numero da
 * confrontare con le fatture del noleggiatore.
 */
export function resocontoPerFornitore(noleggi = [], { dal = "", al = "", projectId = "", oggi = oggiStr() } = {}) {
  const dentro = noleggi.filter(n => {
    if (!n?.dataInizio) return false;
    if (projectId && n.projectId !== projectId) return false;
    const fine = n.dataFineEffettiva || oggi;
    if (al && n.dataInizio > al) return false;
    if (dal && fine < dal) return false;
    return true;
  });
  const mappa = {};
  dentro.forEach(n => {
    const k = n.fornitoreId || "nome:" + chiaveNome(n.fornitore);
    if (!mappa[k]) mappa[k] = { chiave: k, fornitoreId: n.fornitoreId || "", fornitore: n.fornitore || "—", noleggi: [], count: 0, giorni: 0, nolo: 0, trasporti: 0, totale: 0, inCorso: 0, nInCorso: 0, voti: [] };
    const r = mappa[k];
    const nolo = costoNoloNoleggio(n), tr = trasportiNoleggio(n);
    r.noleggi.push({ ...n, _giorni: n.tariffaTipo === "forfettario" ? null : giorniFatturabiliDiNoleggio(n), _nolo: nolo, _consegna: tr.consegna, _ritiro: tr.ritiro, _ritiroDaConfermare: !n.trasportoCompreso && (n.trasportoRitiro === null || n.trasportoRitiro === undefined || n.trasportoRitiro === ""), _totale: nolo + tr.totale });
    r.count += 1;
    r.giorni += n.tariffaTipo === "forfettario" ? 0 : giorniFatturabiliDiNoleggio(n);
    r.nolo += nolo; r.trasporti += tr.totale; r.totale += nolo + tr.totale;
    if (!n.dataFineEffettiva) { r.inCorso += nolo + tr.totale; r.nInCorso += 1; }
    if (n.votoAffidabilita) r.voti.push(n.votoAffidabilita);
    if (n.fornitore) r.fornitore = n.fornitore; // il nome più recente nell'elenco
  });
  const righe = Object.values(mappa).map(r => ({
    ...r,
    noleggi: r.noleggi.sort((a, b) => (b.dataInizio || "").localeCompare(a.dataInizio || "")),
    votoMedio: r.voti.length ? r.voti.reduce((s, v) => s + v, 0) / r.voti.length : null,
  })).sort((a, b) => b.totale - a.totale);
  const tot = righe.reduce((t, r) => ({ count: t.count + r.count, giorni: t.giorni + r.giorni, nolo: t.nolo + r.nolo, trasporti: t.trasporti + r.trasporti, totale: t.totale + r.totale, inCorso: t.inCorso + r.inCorso }), { count: 0, giorni: 0, nolo: 0, trasporti: 0, totale: 0, inCorso: 0 });
  return { righe, totali: tot };
}

/** Fornitori per il menu del noleggio: prima i noleggiatori, poi gli altri; esclusi gli eliminati. */
export function fornitoriPerNoleggio(fornitori = []) {
  const vivi = fornitori.filter(f => !f.eliminato && String(f.denominazione || "").trim());
  const perNome = (a, b) => String(a.denominazione).localeCompare(String(b.denominazione), "it");
  return { noleggiatori: vivi.filter(f => f.noleggiatore).sort(perNome), altri: vivi.filter(f => !f.noleggiatore).sort(perNome) };
}

export function isNoleggioAttivo(n) {
  return !n?.dataFineEffettiva;
}

// Semestre di riferimento di un noleggio, basato sulla data di inizio — usato solo per
// raggruppare il confronto prezzi tra fornitori, non ha altro significato economico.
export function semestreNoleggio(dataInizio) {
  if (!dataInizio) return "";
  const anno = dataInizio.slice(0, 4);
  const mese = parseInt(dataInizio.slice(5, 7), 10);
  return `${anno} · S${mese <= 6 ? "1" : "2"}`;
}

// Soglia oltre la quale mostrare il promemoria di chiusura dimenticata (solo mezzi d'opera,
// vedi ControlloEconomico.js — i veicoli generici non hanno un contatore economico che "sanguina"
// in silenzio, quindi non hanno bisogno dello stesso promemoria).
export const SOGLIA_GIORNI_PROMEMORIA = 30;

export const TIPI_MEZZO_OPERA = { gru:{label:"Gru",emoji:"🏗️"}, piattaforma:{label:"Piattaforma",emoji:"🚧"}, altro_mezzo_opera:{label:"Altro mezzo d'opera",emoji:"🔧"} };
export const TIPI_VEICOLO = { furgone:{label:"Furgone",emoji:"🚐"}, auto:{label:"Auto",emoji:"🚗"}, altro_veicolo:{label:"Altro veicolo",emoji:"🚙"} };

// Costo previsto — calcolato UNA SOLA VOLTA, al momento della creazione (o dello
// spostamento su nuova commessa), da congelare come base di confronto. Non va mai
// ricalcolato dopo, nemmeno se "Modifica" allunga la data fine prevista: è proprio il
// fatto che resti fermo a rendere utile lo scostamento rispetto al costo effettivo, che
// invece cresce giorno per giorno. Solo tariffa giornaliera + data fine prevista note:
// senza una data fine prevista non c'è nulla da pianificare, e il forfettario è già
// per natura un costo fisso, non ha bisogno di un previsto separato. Rispetta le stesse
// esclusioni weekend/festivi impostate sul noleggio, così previsto ed effettivo sono
// sempre calcolati con lo stesso metro; le sospensioni non si applicano al previsto
// (non sono ancora note al momento della creazione).
export function calcolaCostoPrevisto(dataInizio, dataFinePrevista, tariffaTipo, importoGiornaliero, escludiWeekend, escludiFestivi) {
  if (tariffaTipo !== "giornaliera" || !dataFinePrevista || !dataInizio) return null;
  const tariffa = numIt(importoGiornaliero);
  if (!(tariffa > 0)) return null;
  return giorniFatturabiliNoleggio(dataInizio, dataFinePrevista, { escludiWeekend, escludiFestivi }) * tariffa;
}

// SOLO PER DIAGNOSI — traccia giorno per giorno il motivo di inclusione/esclusione.
export function tracciaGiorniFatturabili(dataInizio, dataFineEffettiva, opts = {}) {
  if (!dataInizio) return [];
  const { escludiWeekend = false, escludiFestivi = false, sospensioni = [] } = opts;
  const fineStr = dataFineEffettiva || oggiStr();
  if (fineStr < dataInizio) return [];
  let cursor = new Date(dataInizio + "T00:00:00");
  const end = new Date(fineStr + "T00:00:00");
  const trace = [];
  while (cursor <= end) {
    const cStr = toLocalDateStr(cursor);
    const we = escludiWeekend && isWeekend(cStr);
    const fe = escludiFestivi && isFestivoItaliano(cStr);
    const so = inSospensione(cStr, sospensioni);
    trace.push({ data: cStr, giornoSettimana: cursor.getDay(), escluso: we || fe || so, motivo: we ? "weekend" : fe ? "festivo" : so ? "sospeso" : null });
    cursor.setDate(cursor.getDate() + 1);
  }
  return trace;
}

// ═══════════════════════════════════════════════════════════════════════════
// NOLEGGI DA DECIDERE (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Una gru o una piattaforma dimenticata aperta continua a far crescere il costo della
// commessa giorno dopo giorno. Il costo NON si ferma da solo (se il mezzo è ancora in
// cantiere il fornitore lo fattura): qui si dice solo quando qualcuno deve decidere se
// chiudere il noleggio o prorogarlo. Stessa regola per Registro Noleggi, Cabina di Regia,
// Suggerimenti Operativi e promemoria personali.
//   • "domani" / "oggi"  → la fine prevista è domani / oggi;
//   • "oltre"            → la fine prevista è passata e il noleggio è ancora aperto;
//   • "senza_data"       → nessuna fine prevista (noleggi registrati prima) e aperto da più
//                          di SOGLIA_GIORNI_PROMEMORIA giorni.
// Solo mezzi d'opera (gru, piattaforme…): sono quelli legati a una commessa.
export function scadenzaNoleggio(n, oggi = oggiStr()) {
  if (!n || n.dataFineEffettiva || n.categoria !== "mezzo_opera") return null;
  const tariffaGiorno = n.tariffaTipo === "giornaliera" ? (numIt(n.importoGiornaliero) || 0) : 0;
  // Sospensioni (ottobre 2026): quella in corso oggi e i giorni già sospesi fino a oggi, così
  // l'avviso dice che il noleggio è fermo invece di un generico «oltre la fine prevista».
  const sosp = (n.sospensioni || []).filter(x => x && x.dal && x.al);
  const sospensione = sosp.find(x => oggi >= x.dal && oggi <= x.al) || null;
  const giorniSospesi = sosp.reduce((t, x) => {
    const da = x.dal < (n.dataInizio || x.dal) ? n.dataInizio : x.dal, a = x.al > oggi ? oggi : x.al;
    return a >= da ? t + Math.round((new Date(a + "T00:00:00") - new Date(da + "T00:00:00")) / 86400000) + 1 : t;
  }, 0);
  const base = { costo: costoNoleggio(n), tariffaGiorno, prevista: n.dataFinePrevista || "", sospensione, giorniSospesi };
  if (n.dataFinePrevista) {
    const diff = Math.round((new Date(oggi + "T00:00:00") - new Date(n.dataFinePrevista + "T00:00:00")) / 86400000);
    // fermo in questo momento: nessun costo matura, la decisione si prende alla ripresa
    if (sospensione && diff >= -1) return { ...base, stato: "sospeso", giorni: Math.max(0, diff) };
    if (diff >= 1) return { ...base, stato: "oltre", giorni: diff };
    if (diff === 0) return { ...base, stato: "oggi", giorni: 0 };
    if (diff === -1) return { ...base, stato: "domani", giorni: 0 };
    return null;
  }
  if (sospensione) return null; // senza data e fermo: niente da segnalare finché è sospeso
  const aperto = giorniTrascorsiNoleggio(n.dataInizio, null);
  return aperto > SOGLIA_GIORNI_PROMEMORIA ? { ...base, stato: "senza_data", giorni: aperto } : null;
}

/** Nome leggibile del mezzo: «Gru · AB123CD · Fornitore». */
export function etichettaNoleggio(n) {
  return [TIPI_MEZZO_OPERA[n?.tipo]?.label || TIPI_VEICOLO[n?.tipo]?.label || "Noleggio", n?.identificativo, n?.fornitore].filter(Boolean).join(" · ");
}

/** Frase dell'avviso, uguale ovunque. */
export function testoScadenzaNoleggio(s, dataIt = (d) => d.split("-").reverse().join("/")) {
  if (!s) return "";
  const euro = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const giorno = s.tariffaGiorno > 0 ? ` Ogni giorno in più costa ${euro(s.tariffaGiorno)}.` : "";
  if (s.stato === "domani") return `Finisce domani (${dataIt(s.prevista)}): chiudilo o prorogalo.`;
  if (s.stato === "oggi") return `Finisce oggi: chiudilo o prorogalo.${giorno}`;
  const giaSospeso = s.giorniSospesi > 0 ? ` È stato sospeso per ${s.giorniSospesi} giorn${s.giorniSospesi === 1 ? "o" : "i"}, già esclus${s.giorniSospesi === 1 ? "o" : "i"} dal costo.` : "";
  if (s.stato === "sospeso") {
    const x = s.sospensione || {};
    const fine = s.giorni > 0 ? `doveva finire il ${dataIt(s.prevista)}` : `la fine prevista è il ${dataIt(s.prevista)}`;
    return `Sospeso dal ${dataIt(x.dal)} al ${dataIt(x.al)}${x.motivo ? ` (${x.motivo})` : ""}: in questi giorni non costa. ${fine.charAt(0).toUpperCase() + fine.slice(1)}: alla ripresa chiudilo o prorogalo. Costo maturato ${euro(s.costo)}.`;
  }
  if (s.stato === "oltre") return `Doveva finire il ${dataIt(s.prevista)} (${s.giorni} giorn${s.giorni === 1 ? "o" : "i"} fa) ed è ancora aperto: costo maturato ${euro(s.costo)}.${giaSospeso}${giorno}`;
  return `Aperto da ${s.giorni} giorni senza una data di fine prevista: costo maturato ${euro(s.costo)}.${giorno}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// RETTIFICHE (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Chiudere un noleggio con una data più vecchia di ieri, o correggere la data di fine di
// un noleggio già chiuso, cambia un costo già maturato: è una «rettifica». Richiede un
// motivo, resta per sempre sul noleggio (rettifiche[]) e resta «da vedere» finché un admin
// non la segna come vista (rettificaDaVedere). Avvisa titolari e admin in Nexiora e
// compare in Cabina di Regia e Nexiora Insight.
export function eRettifica(dataFine, giaChiuso, oggi = oggiStr()) {
  if (giaChiuso) return true;
  const ieri = new Date(oggi + "T00:00:00"); ieri.setDate(ieri.getDate() - 1);
  return !!dataFine && dataFine < toLocalDateStr(ieri);
}

/** Rettifiche ancora da vedere, dalla più recente. */
export function rettificheDaVedere(n) {
  return (n?.rettifiche || []).filter(r => !r.vista).sort((a, b) => (b.il || "").localeCompare(a.il || ""));
}

/** Frase di una rettifica: «fine dal 11/10/2026 al 03/10/2026, −€ 2.240,00 · motivo · Nome». */
export function testoRettifica(r, conImporti = true, dataIt = (d) => (d ? d.split("-").reverse().join("/") : "aperto")) {
  if (!r) return "";
  const diff = (Number(r.costoDopo) || 0) - (Number(r.costoPrima) || 0);
  const euro = `${diff < 0 ? "−" : "+"}€ ${Math.abs(diff).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return [`fine ${r.dataPrima ? `dal ${dataIt(r.dataPrima)}` : "(era aperto)"} al ${dataIt(r.dataDopo)}`, conImporti ? euro : null]
    .filter(Boolean).join(", ") + ` · «${r.motivo || "—"}»${r.riferimento ? ` (rif. ${r.riferimento})` : ""} · ${r.nome || "—"}`;
}
